import { SensorSearchItem } from 'features/knowledgeDocumentViewer/types/searchableItems'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType, SensorEntryWrapper } from 'types'

interface UseSensorSearchParams {
  siteId?: string
  searchText: string
  enabled?: boolean
}

interface UseSensorSearchReturn {
  sensors: SensorSearchItem[]
  isLoading: boolean
  totalCount: number
}

/**
 * Hook for searching sensors/instrumentation by name, description, or asset.
 * Transformation is memoized separately from filtering to avoid recreating
 * all sensor objects on every keystroke.
 */
export function useSensorSearch({
  siteId,
  searchText,
  enabled = true,
}: UseSensorSearchParams): UseSensorSearchReturn {
  const loadOptions = useMemo(
    () =>
      siteId && enabled
        ? {
            type: [RolodexEntryType.DATA_POINTS],
            tags: { site: [siteId] },
            limit: 10000,
          }
        : { ids: [] as string[] },
    [siteId, enabled]
  )

  const { data: allSensorWrappers, isLoading } =
    useGetRolodexEntriesByLoadOptions<SensorEntryWrapper[]>(loadOptions)

  const allSensors = useMemo(
    () => allSensorWrappers?.map(transformToSensorSearchItem) ?? [],
    [allSensorWrappers]
  )

  const { sensors, totalCount } = useMemo(() => {
    if (allSensors.length === 0) {
      return { sensors: [], totalCount: 0 }
    }

    if (!searchText.trim()) {
      return { sensors: allSensors, totalCount: allSensors.length }
    }

    const normalizedSearch = searchText.toLowerCase().trim()
    const filtered = allSensors.filter((sensor) =>
      matchesSensorSearch(sensor, normalizedSearch)
    )

    return {
      sensors: filtered,
      totalCount: filtered.length,
    }
  }, [allSensors, searchText])

  return {
    sensors,
    isLoading,
    totalCount,
  }
}

function transformToSensorSearchItem(
  wrapper: SensorEntryWrapper
): SensorSearchItem {
  const entry = wrapper.entry
  return {
    id: entry.id,
    itemType: 'sensor',
    displayName: entry.data.name,
    description: entry.data.description,
    sensorId: entry.id,
    name: entry.data.name,
    unit: entry.data.unit,
    asset: entry.data.asset,
    timestamp: entry.updated_at ?? entry.created_at,
  }
}

function matchesSensorSearch(
  sensor: SensorSearchItem,
  normalizedSearch: string
): boolean {
  const name = sensor.name?.toLowerCase() ?? ''
  const description = sensor.description?.toLowerCase() ?? ''
  const asset = sensor.asset?.toLowerCase() ?? ''

  return (
    name.includes(normalizedSearch) ||
    description.includes(normalizedSearch) ||
    asset.includes(normalizedSearch)
  )
}
